import { WorkbookV2Header } from './WorkbookV2Header';
import { WorkbookV2Content } from './WorkbookV2Content';
import { WorkbookV2Quote } from './WorkbookV2Quote';
import { WorkbookV2List } from './WorkbookV2List';
import { WorkbookV2Verse } from './WorkbookV2Verse';
import { WorkbookV2TextInput } from './WorkbookV2TextInput';
import { DragHandle } from '../❉molecules/SortableList/components/SortableItem/SortableItem';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { faFloppyDisk } from '@fortawesome/free-regular-svg-icons';
import { SimpleModal } from '../❉molecules/SimpleModal';
import { WorkbookV2Checkbox } from './WorkbookV2Checkbox';
import { WorkbookV2Radio } from './WorkbookV2Radio';
import { WorkbookV2Rating } from './WorkbookV2Rating';
import { WorkbookV2Link } from './WorkbookV2Link';
import { WorkbookV2Image } from './WorkbookV2Image';
import { getDataAPI, patchDataAPI } from '../../Helpers/FetchAPI';
import toast from 'react-hot-toast';
import Highlighter from 'web-highlighter';
import { WorkbookV2InlineVideo } from './WorkbookV2InlineVideo';
import { buildWorkbookItemSavePayload, getItemByTemplateApiPath, getTemplateItemId } from '../../utils/workbookV2';

export const WorkbookV2Item = forwardRef(
  (
    {
      item,
      items,
      editor,
      updateItem,
      deleteItem,
      uploadImage,
      userId,
      setVideoUrls = () => {},
      setLessonHighlights = () => {},
      openVideoDrawer,
      openInEdit,
      editingLanguage = null,
      workbook,
      creditId,
      lessonId
    },
    ref
  ) => {
    const [editing, setEditing] = useState(openInEdit ?? false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showPageNumber, setShowPageNumber] = useState(false);
    const [oldAnswer, setOldAnswer] = useState('');
    const [highlighter, setHighlighter] = useState(null);

    const itemRef = useRef(null);

    if (!updateItem) {
      updateItem = () => {};
    }

    useEffect(() => {
      if (!highlighter) return;
      highlighter
        .on('selection:hover', ({ id }) => {
          // display different bg color when hover
          highlighter.addClass('bg-pdred/30', id);
        })
        .on('selection:hover-out', ({ id }) => {
          // remove the hover effect when leaving
          highlighter.removeClass('bg-pdred/30', id);
        })
        .on('selection:click', ({ id }) => {
          highlighter.remove(id);
          const filtered = item.highlights?.filter(({ hs }) => hs?.id !== id) || [];
          patchDataAPI(
            getItemByTemplateApiPath(creditId, getTemplateItemId(item)),
            buildWorkbookItemSavePayload({ highlights: [...filtered], lessonId })
          ).then(() => {
            item.highlights = filtered;
            setLessonHighlights((prev) => prev.filter(({ hs }) => hs?.id !== id));
          });
        })
        .on('selection:create', ({ sources, type }) => {
          if (type === 'from-store') return;
          sources = sources.map((hs) => ({ hs }));
          const payload = [...(item.highlights || []), ...sources];
          patchDataAPI(
            getItemByTemplateApiPath(creditId, getTemplateItemId(item)),
            buildWorkbookItemSavePayload({ highlights: payload, lessonId })
          ).then(() => {
            item.highlights = payload;
            setLessonHighlights((prev) => {
              return [...prev, ...sources];
            });
          });
        });
      setLessonHighlights((prev) => [...prev, ...(item.highlights || [])]);
      item?.highlights?.forEach(
        // hs is the same data saved by 'store.save(sources)'
        ({ hs }) => {
          if (hs && hs.startMeta && hs.endMeta) {
            try {
              highlighter.fromStore(hs.startMeta, hs.endMeta, hs.text, hs.id);
            } catch (e) {
              console.warn('Failed to restore highlight:', hs.id, e);
            }
          }
        }
      );
      highlighter.run();
    }, [highlighter]);

    useEffect(() => {
      const index = items.findIndex((i) => i._id === item._id);
      let previousPageNumber = 0;
      if (index > 0) {
        const pageNumber = items[index - 1].page_number;
        if (pageNumber) {
          previousPageNumber = pageNumber;
        }
      }
      if (item.page_number !== previousPageNumber && item.page_number) {
        setShowPageNumber(true);
      } else {
        setShowPageNumber(false);
      }

      const answerableTypes = ['radio', 'checkbox', 'textInput', 'rating'];
      const hasAnswer = item.answer || item.answers?.length > 0;
    if (userId && answerableTypes.includes(item.type) && item.old_question_id && !hasAnswer) {
      const syncOldWorkbookAnswers = async () => {
        try {
          const res = await getDataAPI(`/userWorkbookItems/${userId}/question/${item.old_question_id}`);

          if (res.data?.length) {
            setOldAnswer(res.data);
            toast.success('Old Workbook Answers Synced', { id: 'ANSWER_SYNC' });
          }
        } catch (error) {
          if (error?.response?.status === 404) return;

          toast.error('Error Syncing Workbook Answers', { id: 'ANSWER_SYNC' });
        }
      };

      syncOldWorkbookAnswers();
    }
      if (!answerableTypes.includes(item.type) && !editor) {
        if (!highlighter) {
          const hl = new Highlighter({ $root: itemRef.current, wrapTag: 'mark' });
          setHighlighter(hl);
        }
      }
    }, [item, items]);

    const renderType = () => {
      switch (item.type) {
        case 'header': {
          return (
            <WorkbookV2Header
              item={item}
              updateItem={(item) => updateItem(item)}
              editing={editing}
              editingLanguage={editingLanguage}
              workbook={workbook}
            ></WorkbookV2Header>
          );
        }
        case 'content': {
          return (
            <WorkbookV2Content
              item={item}
              updateItem={(item) => updateItem(item)}
              editing={editing}
              editingLanguage={editingLanguage}
              workbook={workbook}
            ></WorkbookV2Content>
          );
        }
        case 'quote': {
          return (
            <WorkbookV2Quote
              item={item}
              updateItem={(item) => updateItem(item)}
              editing={editing}
              editingLanguage={editingLanguage}
              workbook={workbook}
            ></WorkbookV2Quote>
          );
        }
        case 'list': {
          return (
            <WorkbookV2List
              item={item}
              updateItem={(item) => updateItem(item)}
              editing={editing}
              editingLanguage={editingLanguage}
              workbook={workbook}
            ></WorkbookV2List>
          );
        }
        case 'verse': {
          return (
            <WorkbookV2Verse
              item={item}
              updateItem={(item) => updateItem(item)}
              editing={editing}
              editingLanguage={editingLanguage}
              workbook={workbook}
            ></WorkbookV2Verse>
          );
        }
        case 'textInput': {
          return (
            <WorkbookV2TextInput
              item={item}
              updateItem={(item) => updateItem(item)}
              editing={editing}
              editor={editor}
              oldAnswer={oldAnswer}
              ref={ref}
              editingLanguage={editingLanguage}
              workbook={workbook}
              creditId={creditId}
              lessonId={lessonId}
            ></WorkbookV2TextInput>
          );
        }
        case 'checkbox': {
          return (
            <WorkbookV2Checkbox
              item={item}
              updateItem={(item) => updateItem(item)}
              editing={editing}
              editor={editor}
              oldAnswer={oldAnswer}
              ref={ref}
              editingLanguage={editingLanguage}
              workbook={workbook}
              creditId={creditId}
              lessonId={lessonId}
            ></WorkbookV2Checkbox>
          );
        }
        case 'radio': {
          return (
            <WorkbookV2Radio
              item={item}
              updateItem={(item) => updateItem(item)}
              editing={editing}
              editor={editor}
              oldAnswer={oldAnswer}
              editingLanguage={editingLanguage}
              workbook={workbook}
              creditId={creditId}
              lessonId={lessonId}
              ref={ref}
            ></WorkbookV2Radio>
          );
        }
        case 'rating': {
          return (
            <WorkbookV2Rating
              item={item}
              updateItem={(item) => updateItem(item)}
              editing={editing}
              editor={editor}
              oldAnswer={oldAnswer}
              editingLanguage={editingLanguage}
              workbook={workbook}
              creditId={creditId}
              lessonId={lessonId}
              ref={ref}
            ></WorkbookV2Rating>
          );
        }
        case 'link': {
          if (item.options?.target?.includes('vimeo.com')) {
            setVideoUrls((prev) => {
              if (prev.includes(item.options.target)) return prev;
              return [...prev, item.options.target];
            });
          }
          return (
            <WorkbookV2Link
              item={item}
              updateItem={(item) => updateItem(item)}
              editing={editing}
              openVideoDrawer={openVideoDrawer}
              editingLanguage={editingLanguage}
              workbook={workbook}
            ></WorkbookV2Link>
          );
        }
        case 'inlineVideo': {
          if (item.options?.target?.includes('vimeo.com')) {
            setVideoUrls((prev) => {
              if (prev.includes(item.options.target)) return prev;
              return [...prev, item.options.target];
            });
          }
          return (
            <WorkbookV2InlineVideo
              item={item}
              updateItem={(item) => updateItem(item)}
              editing={editing}
              editingLanguage={editingLanguage}
              workbook={workbook}
            ></WorkbookV2InlineVideo>
          );
        }
        case 'image': {
          return (
            <WorkbookV2Image
              item={item}
              updateItem={(item) => updateItem(item)}
              editing={editing}
              uploadImage={(image) => uploadImage(image)}
            ></WorkbookV2Image>
          );
        }
        default:
          console.log(item.type);
      }
    };

    return (
      <div className="flex items-center justify-between p-4">
        <div className="flex flex-col gap-2">
          {editor && (
            <>
              <DragHandle />
              {editing ? (
                <FontAwesomeIcon icon={faFloppyDisk} className="size-10" onClick={() => setEditing(!editing)} />
              ) : (
                <PencilSquareIcon className="size-10" onClick={() => setEditing(!editing)} />
              )}
            </>
          )}
        </div>
        <div ref={itemRef} className="w-full">
          {renderType()}
        </div>
        {showPageNumber && (
          <span className="self-start pt-3 text-lg whitespace-nowrap text-gray-400">p. {item.page_number}</span>
        )}
        {editor && (
          <>
            <TrashIcon className="text-pdred/70 size-9" onClick={() => setShowDeleteModal(true)} />
            {showDeleteModal && (
              <SimpleModal hideModal={() => setShowDeleteModal(false)}>
                <div className="flex flex-col items-center gap-2">
                  <span>Are you sure you want to delete this item?</span>
                  <button
                    className="bg-pdred rounded-2xl p-6 text-white shadow-md"
                    onClick={() => deleteItem(item._id)}
                  >
                    Yes
                  </button>
                </div>
              </SimpleModal>
            )}
          </>
        )}
      </div>
    );
  }
);
